import React, { useState, useEffect, useCallback, useRef, useMemo } from "react";
import {
  MessageCircle, Send, RefreshCw, Search, ChevronLeft,
  Clock, CheckCheck, Check, XCircle, Loader2, PenSquare,
  User, Phone, ArrowLeft, Paperclip, Image as ImageIcon, FileText, Smile,
  Bot, Pause, Play, Edit2, Trash2, BarChart3,
} from "lucide-react";
import apiClient from "../../utils/apiClient";
import WhatsAppMediaMessage from "./WhatsAppMediaMessage";
import { AppModal } from "../ui";
import "./whatsapp-chat-inbox.scss";

/* ── Helpers ──────────────────────────────────────────────────────────────── */
const statusIcon = (s) => {
  if (s === "read") return <CheckCheck size={13} className="wai-tick--read" />;
  if (s === "delivered") return <CheckCheck size={13} className="wai-tick--delivered" />;
  if (s === "sent") return <Check size={13} className="wai-tick--sent" />;
  if (s === "failed") return <XCircle size={13} className="wai-tick--failed" />;
  return <Clock size={13} className="wai-tick--pending" />;
};

const formatChatTime = (d) => {
  if (!d) return "";
  const date = new Date(d);
  const now = new Date();
  const diff = now - date;
  const oneDay = 86400000;
  if (diff < oneDay && now.getDate() === date.getDate())
    return date.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
  if (diff < 2 * oneDay) return "Yesterday";
  return date.toLocaleDateString([], { day: "2-digit", month: "2-digit", year: "2-digit" });
};

const formatDateSeparator = (d) => {
  if (!d) return "";
  const date = new Date(d);
  const now = new Date();
  const diff = Math.floor((now - date) / 86400000);
  if (diff === 0) return "Today";
  if (diff === 1) return "Yesterday";
  return date.toLocaleDateString([], { weekday: "long", month: "short", day: "numeric" });
};

const groupByDate = (messages) => {
  const groups = [];
  let currentLabel = "";
  for (const m of messages) {
    const label = formatDateSeparator(m.createdAt);
    if (label !== currentLabel) {
      currentLabel = label;
      groups.push({ type: "date", label });
    }
    groups.push({ type: "msg", data: m });
  }
  return groups;
};

const ChatAvatar = ({ name, phone }) => {
  const initials = name
    ? name.split(" ").filter(Boolean).slice(0, 2).map(w => w[0]).join("").toUpperCase()
    : (phone || "?").slice(-2);
  return <div className="wai-avatar">{initials}</div>;
};

const Loader = () => (
  <div className="wai-loader"><Loader2 size={20} className="wai-spin" /> Loading…</div>
);

/**
 * WhatsAppChatInbox — Shared WhatsApp Web–style inbox component.
 *
 * Props:
 *   fetchConversations(statusFilter) → Promise<normalised[]>
 *     Each item: { _id, name, phone, preview, updatedAt, unreadCount, status, studentName? }
 *   fetchMessages(conversationId) → Promise<messages[]>  (oldest → newest)
 *     Each message: { _id, direction, content, status, createdAt }
 *   sendReply(conversation, text) → Promise
 *   updateStatus(conversationId, status) → Promise
 *   showToast(msg, type)
 *   socket              — Socket.IO instance (from useSocket()). Listens for 'whatsapp:newMessage'.
 */
const WhatsAppChatInbox = ({
  fetchConversations,
  fetchMessages,
  sendReply,
  updateStatus,
  showToast,
  socket,
  // Backend paths used to stream/upload media. Defaults match the institute API;
  // the platform inbox passes "/platform/whatsapp/..." instead.
  mediaEndpoint = "/whatsapp/media",
  uploadEndpoint = "/whatsapp/upload-media",
}) => {
  const [conversations, setConversations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  const [messages, setMessages] = useState([]);
  const [msgLoading, setMsgLoading] = useState(false);
  const [replyText, setReplyText] = useState("");
  const [sending, setSending] = useState(false);
  const [statusFilter, setStatusFilter] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const messagesEndRef = useRef(null);
  /* AI agent state */
  const [aiDrafts, setAiDrafts] = useState([]);
  const [aiDraftEdit, setAiDraftEdit] = useState({}); // { [draftId]: editedText }
  const [aiDraftBusy, setAiDraftBusy] = useState({}); // { [draftId]: 'sending'|'discarding' }
  const [aiPauseBusy, setAiPauseBusy] = useState(false);

  /* ── New Message compose state ───────────────────────────────────────────── */
  const [composing, setComposing] = useState(false);
  const [composePhone, setComposePhone] = useState("");
  const [composeQuery, setComposeQuery] = useState("");
  const [composeResults, setComposeResults] = useState([]);
  const [composeSearching, setComposeSearching] = useState(false);
  const [composeRecipient, setComposeRecipient] = useState(null); // { name, phone } or null for external
  const [composeText, setComposeText] = useState("");
  const [composeSending, setComposeSending] = useState(false);

  // Citation drill-down popover for AI replies grounded in KB chunks
  const [citationPopover, setCitationPopover] = useState(null); // { messageId, ids, items, loading, error }

  // AI Insights drill-down for the selected conversation
  const [aiInsights, setAiInsights] = useState(null); // { open, loading, data, error }
  const composeSearchTimer = useRef(null);

  const handleStartCompose = () => {
    setComposing(true);
    setSelected(null);
    setComposePhone("");
    setComposeQuery("");
    setComposeResults([]);
    setComposeRecipient(null);
    setComposeText("");
  };

  const handleCloseCompose = () => {
    setComposing(false);
    setComposePhone("");
    setComposeQuery("");
    setComposeResults([]);
    setComposeRecipient(null);
    setComposeText("");
  };

  const handleComposeSearch = (value) => {
    setComposeQuery(value);
    if (composeSearchTimer.current) clearTimeout(composeSearchTimer.current);
    if (value.trim().length < 2) { setComposeResults([]); setComposeSearching(false); return; }
    setComposeSearching(true);
    composeSearchTimer.current = setTimeout(async () => {
      try {
        const { data } = await apiClient.get(`/whatsapp/students/search?q=${encodeURIComponent(value.trim())}`);
        setComposeResults(data.students || []);
      } catch { setComposeResults([]); }
      setComposeSearching(false);
    }, 350);
  };

  const handleComposeSelectStudent = (student) => {
    setComposeRecipient({ name: student.name, phone: student.mobile_number, studentId: student._id });
    setComposePhone(student.mobile_number || "");
    setComposeQuery("");
    setComposeResults([]);
  };

  const handleComposeSend = async () => {
    const phone = composePhone.trim();
    if (!phone || phone.length < 10 || !composeText.trim()) return;
    setComposeSending(true);
    try {
      const payload = { messageType: "text", text: composeText.trim() };
      if (composeRecipient?.studentId) {
        payload.studentId = composeRecipient.studentId;
      } else {
        payload.toPhone = phone;
      }
      await apiClient.post("/whatsapp/send-message", payload);
      showToast("Message sent");
      handleCloseCompose();
      loadConversations(false);
    } catch (err) {
      showToast(err?.response?.data?.message || "Failed to send", "error");
    }
    setComposeSending(false);
  };

  /* ── Load conversations ──────────────────────────────────────────────────── */
  const loadConversations = useCallback(async (silent) => {
    if (!silent) setLoading(true);
    try {
      const data = await fetchConversations(statusFilter);
      setConversations(data);
    } catch { /* ignore */ }
    if (!silent) setLoading(false);
  }, [fetchConversations, statusFilter]);

  useEffect(() => { loadConversations(false); }, [loadConversations]);

  // Real-time updates via Socket.IO
  const selectedRef = useRef(null);
  selectedRef.current = selected;

  useEffect(() => {
    if (!socket) return;
    const handler = () => {
      loadConversations(true);
      // If a conversation is open, refresh its messages too
      if (selectedRef.current) {
        fetchMessages(selectedRef.current._id)
          .then(msgs => setMessages(msgs))
          .catch(() => {});
      }
    };
    socket.on('whatsapp:newMessage', handler);
    return () => socket.off('whatsapp:newMessage', handler);
  }, [socket, loadConversations, fetchMessages]);

  /* ── Scroll to bottom ───────────────────────────────────────────────────── */
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  /* ── Open conversation ──────────────────────────────────────────────────── */
  const openConversation = useCallback(async (conv) => {
    setSelected(conv);
    setMsgLoading(true);
    try {
      const msgs = await fetchMessages(conv._id);
      setMessages(msgs);
    } catch { /* ignore */ }
    setMsgLoading(false);
  }, [fetchMessages]);

  /* ── AI: Load pending drafts for selected conversation ─────────────────── */
  const loadAIDrafts = useCallback(async (convId) => {
    if (!convId) { setAiDrafts([]); return; }
    try {
      const { data } = await apiClient.get(`/ai/whatsapp/drafts?status=pending&conversationId=${convId}`);
      setAiDrafts(data.drafts || []);
    } catch (err) {
      // 402/403 = no plan; silently ignore
      if (err?.response?.status !== 402 && err?.response?.status !== 403) {
        // non-fatal
      }
      setAiDrafts([]);
    }
  }, []);

  useEffect(() => { loadAIDrafts(selected?._id); }, [selected?._id, loadAIDrafts]);

  /* ── AI: socket listeners for new drafts / handoffs ──────────────────────── */
  useEffect(() => {
    if (!socket) return;
    const onDraft = (payload) => {
      if (selected?._id === payload?.conversationId) {
        loadAIDrafts(selected._id);
      }
    };
    const onHandoff = (payload) => {
      if (selected?._id === payload?.conversationId) {
        setSelected(prev => prev ? { ...prev, aiPaused: true, aiHandoffReason: payload?.reason || 'AI handoff' } : prev);
        showToast(`AI paused for this conversation: ${payload?.reason || 'handoff'}`, 'info');
      }
      loadConversations(true);
    };
    socket.on('whatsapp:ai:draft', onDraft);
    socket.on('whatsapp:ai:handoff', onHandoff);
    return () => {
      socket.off('whatsapp:ai:draft', onDraft);
      socket.off('whatsapp:ai:handoff', onHandoff);
    };
  }, [socket, selected?._id, loadAIDrafts, loadConversations, showToast]);

  /* ── AI: send / discard draft ───────────────────────────────────────────── */
  const handleSendAIDraft = async (draft) => {
    setAiDraftBusy(b => ({ ...b, [draft._id]: 'sending' }));
    try {
      const editedText = (aiDraftEdit[draft._id] || '').trim();
      const body = editedText && editedText !== draft.draftText ? { editedText } : {};
      await apiClient.post(`/ai/whatsapp/drafts/${draft._id}/send`, body);
      showToast('AI reply sent', 'success');
      setAiDrafts(prev => prev.filter(d => d._id !== draft._id));
      setAiDraftEdit(prev => { const n = { ...prev }; delete n[draft._id]; return n; });
      // Refresh messages
      if (selected?._id) {
        const msgs = await fetchMessages(selected._id);
        setMessages(msgs);
        loadConversations(true);
      }
    } catch (err) {
      showToast(err?.response?.data?.message || 'Failed to send AI draft', 'error');
    } finally {
      setAiDraftBusy(b => { const n = { ...b }; delete n[draft._id]; return n; });
    }
  };
  const handleDiscardAIDraft = async (draft) => {
    setAiDraftBusy(b => ({ ...b, [draft._id]: 'discarding' }));
    try {
      await apiClient.post(`/ai/whatsapp/drafts/${draft._id}/discard`);
      setAiDrafts(prev => prev.filter(d => d._id !== draft._id));
    } catch (err) {
      showToast(err?.response?.data?.message || 'Failed to discard', 'error');
    } finally {
      setAiDraftBusy(b => { const n = { ...b }; delete n[draft._id]; return n; });
    }
  };

  /* ── AI: pause / resume ──────────────────────────────────────────────────── */
  const handleAIPauseToggle = async () => {
    if (!selected) return;
    setAiPauseBusy(true);
    try {
      const path = selected.aiPaused ? 'resume' : 'pause';
      const { data } = await apiClient.post(`/ai/whatsapp/conversations/${selected._id}/${path}`);
      setSelected(prev => ({
        ...prev,
        aiPaused: !!data.conversation?.aiPaused,
        aiHandoffReason: data.conversation?.aiHandoffReason,
      }));
      showToast(selected.aiPaused ? 'AI resumed for this conversation' : 'AI paused for this conversation', 'success');
      loadConversations(true);
    } catch (err) {
      const status = err?.response?.status;
      if (status === 402 || status === 403) {
        showToast('AI WhatsApp Agent not enabled on your plan', 'error');
      } else {
        showToast(err?.response?.data?.message || 'Failed to toggle AI', 'error');
      }
    } finally {
      setAiPauseBusy(false);
    }
  };

  /* ── AI: open citation drill-down ─────────────────────────────────────── */
  const setAiOverride = async (override) => {
    if (!selected) return;
    setAiPauseBusy(true);
    try {
      const { data } = await apiClient.post(`/ai/whatsapp/conversations/${selected._id}/ai-override`, { override });
      setSelected(prev => ({ ...prev, aiOverride: data.aiOverride }));
      const labels = { 'inherit': 'follow global setting', 'force-on': 'always reply (force on)', 'force-off': 'never reply (force off)' };
      showToast(`AI override: ${labels[data.aiOverride] || data.aiOverride}`, 'success');
      loadConversations(true);
    } catch (err) {
      showToast(err?.response?.data?.message || 'Failed to set override', 'error');
    } finally {
      setAiPauseBusy(false);
    }
  };

  const openCitationPopover = async (messageId, ids) => {
    if (!Array.isArray(ids) || ids.length === 0) return;
    setCitationPopover({ messageId, ids, items: [], loading: true, error: null });
    try {
      const { data } = await apiClient.get(`/ai/whatsapp/knowledge/by-ids?ids=${ids.join(',')}`);
      setCitationPopover({ messageId, ids, items: data?.items || [], loading: false, error: null });
    } catch (err) {
      setCitationPopover({
        messageId, ids, items: [], loading: false,
        error: err?.response?.data?.message || 'Failed to load knowledge chunks',
      });
    }
  };

  const closeCitationPopover = () => setCitationPopover(null);

  /* ── AI: open conversation insights drill-down ────────────────────────── */
  const openAIInsights = async () => {
    if (!selected) return;
    setAiInsights({ open: true, loading: true, data: null, error: null });
    try {
      const { data } = await apiClient.get(`/ai/whatsapp/conversations/${selected._id}/analytics`);
      setAiInsights({ open: true, loading: false, data, error: null });
    } catch (err) {
      setAiInsights({
        open: true, loading: false, data: null,
        error: err?.response?.data?.message || 'Failed to load AI insights',
      });
    }
  };
  const closeAIInsights = () => setAiInsights(null);



  /* ── Send reply ─────────────────────────────────────────────────────────── */
  const handleSendReply = async () => {
    if (!replyText.trim() || !selected) return;
    setSending(true);
    try {
      await sendReply(selected, { messageType: "text", text: replyText.trim() });
      setReplyText("");
      // Refresh messages
      const msgs = await fetchMessages(selected._id);
      setMessages(msgs);
      loadConversations(true);
    } catch (err) {
      showToast(err?.response?.data?.message || err?.message || "Failed to send", "error");
    }
    setSending(false);
  };

  /* ── Send attachment (image / sticker / document) ───────────────────────── */
  const fileInputRef = useRef(null);
  const stickerInputRef = useRef(null);
  const docInputRef = useRef(null);
  const [uploading, setUploading] = useState(false);

  const handleAttachmentPicked = async (file, asMessageType) => {
    if (!file || !selected) return;
    setUploading(true);
    try {
      const form = new FormData();
      form.append("file", file);
      const { data } = await apiClient.post(uploadEndpoint, form, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      const messageType = asMessageType
        || (data.mimeType?.startsWith("image/") ? "image"
          : data.mimeType?.startsWith("video/") ? "video"
          : data.mimeType?.startsWith("audio/") ? "audio" : "document");

      await sendReply(selected, {
        messageType,
        mediaId: data.mediaId,
        mimeType: data.mimeType,
        filename: data.filename,
        caption: replyText.trim() || undefined,
      });
      setReplyText("");
      const msgs = await fetchMessages(selected._id);
      setMessages(msgs);
      loadConversations(true);
    } catch (err) {
      showToast(err?.response?.data?.message || err?.message || "Failed to send attachment", "error");
    }
    setUploading(false);
  };

  /* ── Status change ──────────────────────────────────────────────────────── */
  const handleStatusChange = async (convId, status) => {
    try {
      await updateStatus(convId, status);
      loadConversations(true);
      if (selected?._id === convId) setSelected(prev => ({ ...prev, status }));
      showToast(`Conversation ${status}`);
    } catch (err) {
      showToast(err?.message || "Failed", "error");
    }
  };

  /* ── Filter conversations ───────────────────────────────────────────────── */
  const filtered = conversations.filter(c => {
    if (!searchTerm) return true;
    const q = searchTerm.toLowerCase();
    return (c.name || "").toLowerCase().includes(q)
      || (c.phone || "").includes(q)
      || (c.studentName || "").toLowerCase().includes(q);
  });

  const grouped = groupByDate(messages);

  return (
    <div className="wai-container">
      {/* ════════ Left: Conversation list ════════ */}
      <div className={`wai-sidebar${selected ? " wai-sidebar--hidden-mobile" : ""}`}>
        <div className="wai-sidebar__header">
          <span className="wai-sidebar__title">Chats</span>
          <div className="wai-sidebar__actions">
            <select
              className="wai-filter-select"
              value={statusFilter}
              onChange={e => setStatusFilter(e.target.value)}
            >
              <option value="">All</option>
              <option value="open">Open</option>
              <option value="assigned">Assigned</option>
              <option value="resolved">Resolved</option>
              <option value="closed">Closed</option>
            </select>
            <button className="wai-icon-btn" onClick={() => loadConversations(false)} title="Refresh">
              <RefreshCw size={16} />
            </button>
            <button className="wai-icon-btn" onClick={handleStartCompose} title="New message">
              <PenSquare size={16} />
            </button>
          </div>
        </div>

        <div className="wai-search">
          <Search size={15} className="wai-search__icon" />
          <input
            className="wai-search__input"
            placeholder="Search or start new chat"
            value={searchTerm}
            onChange={e => setSearchTerm(e.target.value)}
          />
        </div>

        <div className="wai-conv-list">
          {loading ? <Loader /> : filtered.length === 0 ? (
            <div className="wai-empty-sidebar">
              <MessageCircle size={28} />
              <span>No conversations</span>
            </div>
          ) : (
            filtered.map(c => (
              <div
                key={c._id}
                role="button"
                tabIndex={0}
                className={`wai-conv-item${selected?._id === c._id ? " wai-conv-item--active" : ""}`}
                onClick={() => openConversation(c)}
                onKeyDown={e => { if (e.key === "Enter") openConversation(c); }}
              >
                <ChatAvatar name={c.name} phone={c.phone} />
                <div className="wai-conv-item__body">
                  <div className="wai-conv-item__top">
                    <span className="wai-conv-item__name">{c.name || c.phone}</span>
                    <span className="wai-conv-item__time">{formatChatTime(c.updatedAt)}</span>
                  </div>
                  <div className="wai-conv-item__bottom">
                    <span className="wai-conv-item__preview">{c.preview || "No messages yet"}</span>
                    {c.unreadCount > 0 && <span className="wai-unread-badge">{c.unreadCount}</span>}
                  </div>
                </div>
              </div>
            ))
          )}
        </div>
      </div>

      {/* ════════ Right: Chat area ════════ */}
      <div className={`wai-chat${selected ? " wai-chat--visible-mobile" : ""}`}>
        {composing ? (
          /* ── New Message compose panel ─── */
          <div className="wai-compose">
            <div className="wai-compose__header">
              <button className="wai-icon-btn" onClick={handleCloseCompose}><ArrowLeft size={18} /></button>
              <span className="wai-compose__title">New message</span>
            </div>

            {/* Recipient input */}
            <div className="wai-compose__to">
              <span className="wai-compose__to-label">To:</span>
              {composeRecipient ? (
                <div className="wai-compose__recipient-chip">
                  <User size={13} />
                  <span>{composeRecipient.name || composeRecipient.phone}</span>
                  <button className="wai-compose__chip-x" onClick={() => { setComposeRecipient(null); setComposePhone(""); }}>
                    <XCircle size={13} />
                  </button>
                </div>
              ) : (
                <input
                  className="wai-compose__to-input"
                  placeholder="Type a name or phone number"
                  value={composeQuery || composePhone}
                  onChange={e => {
                    const v = e.target.value;
                    // If it looks like a phone number (starts with digit or +), put it in phone
                    if (/^[+\d]/.test(v) && !/[a-zA-Z]/.test(v)) {
                      setComposePhone(v);
                      setComposeQuery("");
                      setComposeResults([]);
                    } else {
                      setComposePhone("");
                      handleComposeSearch(v);
                    }
                  }}
                  autoFocus
                />
              )}
            </div>

            {/* Search results dropdown */}
            {composeResults.length > 0 && !composeRecipient && (
              <div className="wai-compose__results">
                {composeResults.map(s => (
                  <div key={s._id} className="wai-compose__result-item" onClick={() => handleComposeSelectStudent(s)}>
                    <div className="wai-avatar" style={{ width: 32, height: 32, minWidth: 32, fontSize: '0.7rem' }}>
                      {(s.name || "?").split(" ").slice(0, 2).map(w => w[0]).join("").toUpperCase()}
                    </div>
                    <div>
                      <div style={{ fontWeight: 500, fontSize: '0.85rem', color: 'var(--text-primary, #111b21)' }}>{s.name}</div>
                      <div style={{ fontSize: '0.75rem', color: 'var(--text-secondary, #667781)' }}>{s.mobile_number}</div>
                    </div>
                  </div>
                ))}
              </div>
            )}
            {composeSearching && (
              <div className="wai-loader" style={{ padding: '0.75rem 0' }}><Loader2 size={16} className="wai-spin" /> Searching…</div>
            )}

            {/* Message area */}
            <div className="wai-compose__body">
              <div className="wai-compose__body-empty">
                <MessageCircle size={40} strokeWidth={1} style={{ color: 'var(--text-tertiary, #8696a0)' }} />
                <p style={{ color: 'var(--text-tertiary, #8696a0)', fontSize: '0.85rem', margin: 0 }}>
                  {composeRecipient || composePhone.length >= 10
                    ? "Type your message below"
                    : "Search for a student or enter a phone number"}
                </p>
              </div>
            </div>

            {/* Compose reply bar */}
            <div className="wai-reply">
              <input
                className="wai-reply__input"
                value={composeText}
                onChange={e => setComposeText(e.target.value)}
                placeholder="Type a message"
                disabled={!composeRecipient && composePhone.trim().length < 10}
                onKeyDown={e => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); handleComposeSend(); } }}
              />
              <button
                className="wai-reply__send"
                onClick={handleComposeSend}
                disabled={composeSending || !composeText.trim() || (!composeRecipient && composePhone.trim().length < 10)}
              >
                {composeSending ? <Loader2 size={18} className="wai-spin" /> : <Send size={18} />}
              </button>
            </div>
          </div>
        ) : !selected ? (
          <div className="wai-chat-empty">
            <div className="wai-chat-empty__icon"><MessageCircle size={56} strokeWidth={1} /></div>
            <h3>WhatsApp Inbox</h3>
            <p>Select a conversation to view messages</p>
          </div>
        ) : (
          <>
            {/* Header */}
            <div className="wai-chat__header">
              <button className="wai-back-btn" onClick={() => { setSelected(null); loadConversations(false); }}>
                <ChevronLeft size={20} />
              </button>
              <ChatAvatar name={selected.name} phone={selected.phone} />
              <div className="wai-chat__contact">
                <span className="wai-chat__contact-name">{selected.name || selected.phone}</span>
                <span className="wai-chat__contact-phone">{selected.phone}</span>
              </div>
              <div className="wai-chat__header-actions">
                <span className={`wai-status-pill wai-status-pill--${selected.status}`}>{selected.status}</span>
                {selected.aiPaused ? (
                  <button
                    className="wai-action-btn"
                    onClick={handleAIPauseToggle}
                    disabled={aiPauseBusy}
                    title={selected.aiHandoffReason || 'AI is paused for this conversation'}
                  >
                    {aiPauseBusy ? <Loader2 size={14} className="wai-spin" /> : <Play size={14} />} Resume AI
                  </button>
                ) : (
                  <button
                    className="wai-action-btn"
                    onClick={handleAIPauseToggle}
                    disabled={aiPauseBusy}
                    title="Pause AI auto-replies for this conversation"
                  >
                    {aiPauseBusy ? <Loader2 size={14} className="wai-spin" /> : <Pause size={14} />} Pause AI
                  </button>
                )}
                <button
                  className="wai-action-btn"
                  onClick={openAIInsights}
                  title="View AI metrics for this conversation"
                >
                  <BarChart3 size={14} /> AI Insights
                </button>
                <select
                  className="wai-action-btn"
                  value={selected.aiOverride || 'inherit'}
                  onChange={(e) => setAiOverride(e.target.value)}
                  disabled={aiPauseBusy}
                  title="Per-conversation AI override (overrides global mode for this contact only)"
                  style={{ padding: '4px 6px', fontSize: 12 }}
                >
                  <option value="inherit">AI: Inherit global</option>
                  <option value="force-on">AI: Force ON (always auto-reply)</option>
                  <option value="force-off">AI: Force OFF (never reply)</option>
                </select>
                {selected.status !== "resolved" && (
                  <button className="wai-action-btn" onClick={() => handleStatusChange(selected._id, "resolved")}>
                    <Check size={14} /> Resolve
                  </button>
                )}
                {selected.status !== "closed" && (
                  <button className="wai-action-btn wai-action-btn--close" onClick={() => handleStatusChange(selected._id, "closed")}>
                    <XCircle size={14} /> Close
                  </button>
                )}
              </div>
            </div>

            {/* Messages */}
            <div className="wai-messages">
              {msgLoading ? <Loader /> : messages.length === 0 ? (
                <div className="wai-messages-empty">No messages in this conversation</div>
              ) : (
                grouped.map((item, i) =>
                  item.type === "date" ? (
                    <div key={`d-${i}`} className="wai-date-sep">
                      <span className="wai-date-sep__label">{item.label}</span>
                    </div>
                  ) : (
                    <div
                      key={item.data._id || i}
                      className={`wai-bubble-row${item.data.direction === "outbound" ? " wai-bubble-row--out" : " wai-bubble-row--in"}`}
                    >
                      <div className={`wai-bubble${item.data.direction === "outbound" ? " wai-bubble--out" : " wai-bubble--in"}`}>
                        {item.data.messageType && item.data.messageType !== "text" && item.data.messageType !== "template" && (
                          <div className="wai-bubble__media" style={{ marginBottom: item.data.content ? 6 : 0 }}>
                            <WhatsAppMediaMessage message={item.data} mediaEndpoint={mediaEndpoint} />
                          </div>
                        )}
                        {item.data.content && !/^\[(image|sticker|video|audio|document|location|contacts|interactive|reaction)\]$/i.test(item.data.content.trim()) && (
                          <span className="wai-bubble__text">{item.data.content}</span>
                        )}
                        <span className="wai-bubble__meta">
                          {item.data.generatedBy === 'ai' && (
                            <span className="wai-bubble__ai-badge" title={`AI reply${item.data.aiMeta?.confidence ? ' (confidence ' + Math.round(item.data.aiMeta.confidence * 100) + '%)' : ''}${item.data.aiMeta?.intent ? ' · ' + item.data.aiMeta.intent : ''}`}>
                              <Bot size={11} /> AI
                              {Array.isArray(item.data.aiMeta?.knowledgeChunkIds) && item.data.aiMeta.knowledgeChunkIds.length > 0 && (
                                <button
                                  type="button"
                                  className="wai-bubble__ai-citations"
                                  title={`Grounded in ${item.data.aiMeta.knowledgeChunkIds.length} knowledge chunk${item.data.aiMeta.knowledgeChunkIds.length === 1 ? '' : 's'} — click to view`}
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    openCitationPopover(item.data._id, item.data.aiMeta.knowledgeChunkIds);
                                  }}
                                  style={{
                                    marginLeft: 4, opacity: 0.85, background: 'transparent',
                                    border: 'none', padding: 0, cursor: 'pointer', font: 'inherit', color: 'inherit',
                                  }}
                                >
                                  · 📚 {item.data.aiMeta.knowledgeChunkIds.length}
                                </button>
                              )}
                            </span>
                          )}
                          {new Date(item.data.createdAt).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}
                          {item.data.direction === "outbound" && (
                            <span className="wai-bubble__status">{statusIcon(item.data.status)}</span>
                          )}
                        </span>
                      </div>
                    </div>
                  )
                )
              )}
              <div ref={messagesEndRef} />
            </div>

            {/* AI Draft suggestions */}
            {aiDrafts.length > 0 && (
              <div className="wai-ai-drafts">
                <div className="wai-ai-drafts__header">
                  <Bot size={14} /> AI suggested {aiDrafts.length === 1 ? 'reply' : 'replies'}
                  <span className="wai-ai-drafts__hint">— review and send, edit, or discard</span>
                </div>
                {aiDrafts.map(d => {
                  const editing = aiDraftEdit[d._id] !== undefined;
                  const text = editing ? aiDraftEdit[d._id] : d.draftText;
                  const busy = aiDraftBusy[d._id];
                  return (
                    <div key={d._id} className="wai-ai-draft">
                      <textarea
                        className="wai-ai-draft__text"
                        value={text}
                        onChange={(e) => setAiDraftEdit(prev => ({ ...prev, [d._id]: e.target.value }))}
                        rows={Math.min(6, Math.max(2, Math.ceil(text.length / 60)))}
                      />
                      <div className="wai-ai-draft__actions">
                        {d.intent && <span className="wai-ai-draft__intent">{d.intent}</span>}
                        {typeof d.confidence === 'number' && (
                          <span className="wai-ai-draft__confidence">conf {Math.round(d.confidence * 100)}%</span>
                        )}
                        <div style={{ flex: 1 }} />
                        <button
                          className="wai-action-btn wai-action-btn--close"
                          onClick={() => handleDiscardAIDraft(d)}
                          disabled={!!busy}
                          title="Discard this draft"
                        >
                          {busy === 'discarding' ? <Loader2 size={13} className="wai-spin" /> : <Trash2 size={13} />} Discard
                        </button>
                        <button
                          className="wai-action-btn"
                          onClick={() => handleSendAIDraft(d)}
                          disabled={!!busy}
                        >
                          {busy === 'sending' ? <Loader2 size={13} className="wai-spin" /> : <Send size={13} />}
                          {editing && aiDraftEdit[d._id] !== d.draftText ? ' Send edited' : ' Send'}
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}

            {/* Reply bar */}
            <div className="wai-reply">
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*,video/mp4,audio/*"
                style={{ display: "none" }}
                onChange={(e) => { const f = e.target.files?.[0]; e.target.value = ""; handleAttachmentPicked(f); }}
              />
              <input
                ref={stickerInputRef}
                type="file"
                accept="image/webp"
                style={{ display: "none" }}
                onChange={(e) => { const f = e.target.files?.[0]; e.target.value = ""; handleAttachmentPicked(f, "sticker"); }}
              />
              <input
                ref={docInputRef}
                type="file"
                accept=".pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.txt,.csv,.zip"
                style={{ display: "none" }}
                onChange={(e) => { const f = e.target.files?.[0]; e.target.value = ""; handleAttachmentPicked(f, "document"); }}
              />
              <button
                className="wai-reply__attach"
                title="Attach image / video"
                onClick={() => fileInputRef.current?.click()}
                disabled={uploading || sending}
                style={{ background: "transparent", border: "none", color: "#54656f", padding: 6, cursor: "pointer" }}
              >
                <ImageIcon size={20} />
              </button>
              <button
                className="wai-reply__attach"
                title="Send sticker (.webp)"
                onClick={() => stickerInputRef.current?.click()}
                disabled={uploading || sending}
                style={{ background: "transparent", border: "none", color: "#54656f", padding: 6, cursor: "pointer" }}
              >
                <Smile size={20} />
              </button>
              <button
                className="wai-reply__attach"
                title="Attach document"
                onClick={() => docInputRef.current?.click()}
                disabled={uploading || sending}
                style={{ background: "transparent", border: "none", color: "#54656f", padding: 6, cursor: "pointer" }}
              >
                <Paperclip size={20} />
              </button>
              <input
                className="wai-reply__input"
                value={replyText}
                onChange={e => setReplyText(e.target.value)}
                placeholder={uploading ? "Uploading attachment…" : "Type a message"}
                disabled={uploading}
                onKeyDown={e => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); handleSendReply(); } }}
              />
              <button
                className="wai-reply__send"
                onClick={handleSendReply}
                disabled={sending || uploading || !replyText.trim()}
              >
                {uploading ? <Loader2 size={18} className="wai-spin" /> : <Send size={18} />}
              </button>
            </div>
          </>
        )}
      </div>

      {/* AI Insights — per-conversation analytics drill-down */}
      <AppModal
        show={!!aiInsights?.open}
        onHide={closeAIInsights}
        size="lg"
        icon={<BarChart3 size={18} />}
        title={`AI Insights — ${selected?.name || selected?.phone || ''}`}
      >
        {aiInsights?.loading && (
          <div style={{ textAlign: 'center', padding: 30, color: 'var(--text-secondary, #666)' }}>
            <Loader2 size={20} className="wai-spin" /> Loading…
          </div>
        )}
        {aiInsights?.error && (
          <div style={{ color: '#c0392b', padding: 12, background: 'rgba(192,57,43,0.08)', borderRadius: 4, fontSize: 13 }}>
            {aiInsights.error}
          </div>
        )}
        {!aiInsights?.loading && !aiInsights?.error && aiInsights?.data && (() => {
              const { stats, conversation: conv, recentReplies } = aiInsights.data;
              const reply2InboundRatio = stats.inboundCount > 0
                ? (stats.aiReplies / stats.inboundCount).toFixed(2) : '—';
              const avgCostPerReply = stats.aiReplies > 0
                ? (stats.totalCostUsd / stats.aiReplies) : 0;
              return (
                <>
                  {/* Top stats grid */}
                  <div style={{
                    display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))',
                    gap: 8, marginBottom: 16,
                  }}>
                    {[
                      { label: 'AI replies', value: stats.aiReplies },
                      { label: 'Followups sent', value: stats.followupReplies },
                      { label: 'Reply / inbound', value: reply2InboundRatio },
                      { label: 'Total cost', value: `$${stats.totalCostUsd.toFixed(4)}` },
                      { label: 'Avg cost / reply', value: `$${avgCostPerReply.toFixed(5)}` },
                      { label: 'Tokens (in/out)', value: `${stats.totalPromptTokens}/${stats.totalCompletionTokens}` },
                      { label: 'Avg confidence', value: stats.avgConfidence !== null ? `${Math.round(stats.avgConfidence * 100)}%` : '—' },
                      { label: 'KB citations used', value: stats.totalCitations },
                    ].map((s) => (
                      <div key={s.label} style={{
                        padding: 10, background: '#f8fafc', borderRadius: 6, border: '1px solid #e2e8f0',
                      }}>
                        <div style={{ fontSize: 11, color: '#64748b', marginBottom: 2 }}>{s.label}</div>
                        <div style={{ fontSize: 16, fontWeight: 600, color: '#0f172a' }}>{s.value}</div>
                      </div>
                    ))}
                  </div>

                  {/* Conversation state */}
                  <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 16, fontSize: 12 }}>
                    {conv.aiPaused && (
                      <span style={{ padding: '3px 8px', background: '#fef3c7', color: '#92400e', borderRadius: 4 }}>
                        AI paused
                      </span>
                    )}
                    {conv.aiHandoffAt && (
                      <span style={{ padding: '3px 8px', background: '#fee2e2', color: '#991b1b', borderRadius: 4 }}>
                        Handoff: {conv.aiHandoffReason || 'unknown'} at {new Date(conv.aiHandoffAt).toLocaleString()}
                      </span>
                    )}
                    {conv.aiFollowupOptOut && (
                      <span style={{ padding: '3px 8px', background: '#e0e7ff', color: '#3730a3', borderRadius: 4 }}>
                        Followup opt-out
                      </span>
                    )}
                    {Array.isArray(stats.uniqueIntents) && stats.uniqueIntents.length > 0 && (
                      <span style={{ padding: '3px 8px', background: '#ecfeff', color: '#155e75', borderRadius: 4 }}>
                        Intents: {stats.uniqueIntents.join(', ')}
                      </span>
                    )}
                  </div>

                  {/* Recent replies table */}
                  <h4 style={{ fontSize: 13, marginBottom: 8, color: '#334155' }}>
                    Recent AI replies (last {recentReplies.length})
                  </h4>
                  {recentReplies.length === 0 ? (
                    <div style={{ color: '#999', fontSize: 13, fontStyle: 'italic', padding: 12 }}>
                      No AI replies yet for this conversation.
                    </div>
                  ) : (
                    <div style={{ overflowX: 'auto' }}>
                      <table style={{ width: '100%', fontSize: 12, borderCollapse: 'collapse' }}>
                        <thead>
                          <tr style={{ borderBottom: '1px solid #e2e8f0', textAlign: 'left' }}>
                            <th style={{ padding: '6px 8px' }}>When</th>
                            <th style={{ padding: '6px 8px' }}>Preview</th>
                            <th style={{ padding: '6px 8px', textAlign: 'right' }}>Conf.</th>
                            <th style={{ padding: '6px 8px', textAlign: 'right' }}>Cost</th>
                            <th style={{ padding: '6px 8px', textAlign: 'right' }}>📚</th>
                          </tr>
                        </thead>
                        <tbody>
                          {recentReplies.map((r) => (
                            <tr key={r._id} style={{ borderBottom: '1px solid #f1f5f9' }}>
                              <td style={{ padding: '6px 8px', whiteSpace: 'nowrap', color: '#64748b' }}>
                                {new Date(r.createdAt).toLocaleString([], {
                                  month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit',
                                })}
                                {r.followup && <span title="Proactive followup" style={{ marginLeft: 4, color: '#7c3aed' }}>↻</span>}
                              </td>
                              <td style={{ padding: '6px 8px', color: '#334155' }}>{r.preview}…</td>
                              <td style={{
                                padding: '6px 8px', textAlign: 'right',
                                color: r.confidence < 0.55 ? '#dc2626' : r.confidence < 0.75 ? '#d97706' : '#059669',
                              }}>
                                {r.confidence !== null ? `${Math.round(r.confidence * 100)}%` : '—'}
                              </td>
                              <td style={{ padding: '6px 8px', textAlign: 'right', color: '#64748b' }}>
                                ${(r.costUsd || 0).toFixed(5)}
                              </td>
                              <td style={{ padding: '6px 8px', textAlign: 'right', color: '#64748b' }}>
                                {r.citationCount > 0 ? r.citationCount : '—'}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </>
              );
            })()}
      </AppModal>

      {/* Citation drill-down modal — shows KB chunks that grounded an AI reply */}
      <AppModal
        show={!!citationPopover}
        onHide={closeCitationPopover}
        size="md"
        title={`Knowledge sources used (${citationPopover?.ids?.length || 0})`}
        icon={<span style={{ fontSize: 16 }}>📚</span>}
      >
        <p style={{ fontSize: 12, color: 'var(--text-secondary, #666)', margin: '0 0 12px' }}>
          These knowledge base chunks were retrieved by RAG and included as context for this AI reply.
        </p>
        {citationPopover?.loading && (
          <div style={{ textAlign: 'center', padding: 20, color: 'var(--text-secondary, #666)' }}>
            <Loader2 size={20} className="wai-spin" /> Loading…
          </div>
        )}
        {citationPopover?.error && (
          <div style={{ color: '#c0392b', padding: 12, background: 'rgba(192,57,43,0.08)', borderRadius: 4, fontSize: 13 }}>
            {citationPopover.error}
          </div>
        )}
        {!citationPopover?.loading && !citationPopover?.error && citationPopover?.items?.length === 0 && (
          <div style={{ color: 'var(--text-tertiary, #999)', padding: 12, fontSize: 13, fontStyle: 'italic' }}>
            The referenced knowledge chunks could not be loaded. They may have been deleted.
          </div>
        )}
        {!citationPopover?.loading && (citationPopover?.items || []).map((chunk, idx) => (
          <div
            key={chunk._id}
            style={{
              borderLeft: '3px solid #4a90e2', padding: '8px 12px', marginBottom: 10,
              background: 'var(--bg-secondary, #f8fafc)', borderRadius: '0 4px 4px 0',
            }}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: 4 }}>
              <strong style={{ fontSize: 13, color: 'var(--text-primary, #222)' }}>
                {idx + 1}. {chunk.title || '(untitled)'}
              </strong>
              <span style={{ fontSize: 10, color: 'var(--text-tertiary, #888)', textTransform: 'uppercase' }}>
                {chunk.source || 'manual'}
              </span>
            </div>
            <div style={{ fontSize: 12, color: 'var(--text-secondary, #444)', lineHeight: 1.5, whiteSpace: 'pre-wrap' }}>
              {chunk.text}
            </div>
            {Array.isArray(chunk.tags) && chunk.tags.length > 0 && (
              <div style={{ marginTop: 6 }}>
                {chunk.tags.map((t) => (
                  <span key={t} style={{
                    fontSize: 10, padding: '2px 6px', marginRight: 4, background: 'rgba(21,101,192,0.12)',
                    color: '#1565c0', borderRadius: 10,
                  }}>{t}</span>
                ))}
              </div>
            )}
          </div>
        ))}
      </AppModal>
    </div>
  );
};

export default WhatsAppChatInbox;
